import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';

export type TeamContext =
  | { role: 'owner'; canUseTeams: boolean; seatsLimit: number | null; seatsUsed: number }
  | { role: 'member'; ownerName: string | null; sections: string[]; allAccounts: boolean; accountIds: string[] };

export type User = {
  id: string;
  email: string;
  name: string | null;
  role: 'user' | 'admin';
  timezone: string;
  hasPassword: boolean;
  createdAt: string;
  /** Team context (added by /api/auth/me). Absent right after login until refresh. */
  team?: TeamContext;
  /** Whether the workspace has an active/trial subscription. Added by /api/auth/me;
   *  absent right after login/register until the first refresh (treated as false). */
  entitled?: boolean;
};

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, timezone?: string, name?: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const data = await api<{ user: User }>('/api/auth/me');
      setUser(data.user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setUser(null);
      else throw err;
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    await api<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    // Re-fetch via /me so the user carries full team context.
    await refresh();
  };

  const register = async (email: string, password: string, timezone?: string, name?: string) => {
    const data = await api<{ user: User }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, timezone, name }),
    });
    await refresh();
    return data.user;
  };

  const logout = async () => {
    await api('/api/auth/logout', { method: 'POST' });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
